import React, { ChangeEventHandler, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import axios, { CancelTokenSource } from 'axios';
import { debounce } from '@apps/shared/src/utilities';
import { setSearchInput } from '@apps/shared/src/providerSearch/actions';
import { RootState } from '../store';

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-input': {
    padding: '.6rem',
  },
});

const StyledInfoIcon = styled(InfoIcon)({
  cursor: 'pointer',
  marginLeft: '5px',
  marginBottom: '8px',
  color: '#888787',
});

const StyledTooltip = styled(Tooltip)({
  '& .MuiTooltip-tooltip': {
    backgroundColor: 'transparent',
    padding: 0,
    margin: 0,
    boxShadow: 'none',
    maxWidth: 'none',
  },
  '& .MuiTooltip-arrow': {
    color: '#f5f5f5',
  },
});

const mapStateToProps = ({ providerSearch, planSelect }: RootState): StateProps => ({
  isLoading: providerSearch.isLoadingProviderList,
  selectedZipCode: providerSearch.selectedZipCode,
  searchInput: providerSearch.searchInput,
  selectedRadius: providerSearch.radius,
  selectedAccessibilities: providerSearch.selectedAccessibilities,
  providerType: providerSearch.selectedProviderType,
  providerSpeciality: providerSearch.selectedProviderSpecialty,
  gender: providerSearch.providergendercode,
  entitytype: providerSearch.entitytype,
  planID: planSelect.selectedPlan?.id,
});

type StateProps = {
  isLoading: boolean;
  selectedZipCode: string;
  searchInput: string;
  selectedRadius: string;
  selectedAccessibilities: any[];
  providerType: string;
  providerSpeciality: string;
  gender: string;
  entitytype: string;
  planID: any;
};

type ParentProps = {
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
};

const mapDispatchToProps = { setSearchInput };

type Props = StateProps & ParentProps & typeof mapDispatchToProps;

export function SearchBar({
  setSearchInput,
  searchInput,
  isLoading,
  input,
  setInput,
  planID,
  selectedZipCode,
  selectedRadius,
  selectedAccessibilities,
  providerSpeciality,
  providerType,
  gender,
  entitytype,
}: Props): JSX.Element {
  const cancelSourceRef = useRef<CancelTokenSource | null>(null);

  const debouncedSearch = useCallback(
    debounce((input: string, zip: string) => {
      if (cancelSourceRef.current) {
        cancelSourceRef.current.cancel('Cancelled previous request');
      }

      const source = axios.CancelToken.source();
      cancelSourceRef.current = source;

      setSearchInput(
        planID,
        zip,
        input,
        selectedRadius,
        selectedAccessibilities,
        providerType,
        providerSpeciality,
        gender,
        entitytype,
        source.token
      );
    }, 500),
    [
      setSearchInput,
      planID,
      selectedRadius,
      selectedAccessibilities,
      providerType,
      providerSpeciality,
      gender,
      entitytype,
    ]
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = e => {
    const inputValue = e.currentTarget.value;
    setInput(inputValue);
    debouncedSearch(inputValue, selectedZipCode);
  };

  const clearInput = (): void => {
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel('Cancelled due to clear input');
    }

    setSearchInput(
      planID,
      selectedZipCode,
      '',
      selectedRadius,
      selectedAccessibilities,
      providerType,
      providerSpeciality,
      gender,
      entitytype
    );
    setInput('');
  };

  useEffect(() => {
    setInput(searchInput);
  }, [searchInput]);

  return (
    <div>
      {/* <InfoTooltip tooltip="Search providers by name, address, or phone">
        <Typography className={classes.header}>Search Providers</Typography>
      </InfoTooltip> */}
      <StyledTooltip
        title={
          <div
            style={{
              backgroundColor: '#f5f5f5',
              color: '#000',
              padding: '10px 15px',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: 500,
              width: '250px',
              boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            Free text search supports the following parameters:
            <div style={{ display: 'flex', gap: '2rem', marginTop: '8px' }}>
              <ul style={{ paddingLeft: '15px', margin: 0 }}>
                <li>Provider Name</li>
                <li>Street Address</li>
              </ul>
              <ul style={{ paddingLeft: '15px', margin: 0 }}>
                <li>Provider NPI</li>
                <li>Phone Number</li>
              </ul>
            </div>
          </div>
        }
        placement="right"
        arrow
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
          ],
        }}
      >
        <StyledInfoIcon />
      </StyledTooltip>
      <StyledTextField
        id="search-bar"
        value={input}
        variant="outlined"
        fullWidth
        placeholder="Search..."
        aria-label="Search Providers"
        onChange={handleChange}
        autoComplete="off"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: input && (
            <InputAdornment position="end">
              <IconButton id="search-clear" onClick={clearInput} disabled={isLoading} size="large">
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </div>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchBar);
